import React, { ChangeEvent, useCallback, useRef, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { Controller } from 'react-hook-form';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useDebounce from '~/hooks/useDebounce';
import api from '~/services/api';
import { Lista } from './Lista';
import { CategoriaProps } from './protocols';
import { InputContainer } from './styles';

/**
 * @param {CategoriaProps} Categoria
 */
export const Categoria: React.FC<CategoriaProps> = ({
  label = '',
  placeholder = '',
  ListaWidth = '100%',
  name,
  register,
  isError,
  control,
  changeSelected,
  tipoCategoria = 'Ambos',
  retriever,
  hideNoneOption = false,
  noneOptionLabel = 'Nenhuma',
  ...rest
}: CategoriaProps) => {
  const { debouncedFn } = useDebounce();
  const inputRef: any = useRef(null);

  const [selected, setSelected] = useState<any>({
    label: '',
    value: 0,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const limit = 10;
  const queryClient = useQueryClient();

  const tipoCategoriaMap: any = {
    Receita: 0,
    Despesa: 1,
    Ambos: 2,
  };

  const [categoriaTemp, setCategoriaTemp] = useState<number | null>(
    tipoCategoriaMap[tipoCategoria],
  );

  const filterCategorias = useCallback(
    (options: any[]): any[] => {
      const filteredOptions = options.filter((item) =>
        item.label.toLowerCase().includes(filter.toLowerCase()),
      );
      return filteredOptions;
    },
    [filter],
  );

  const fetchData = useCallback(
    async ({ pageParam = 1 }): Promise<any> => {
      try {
        const tipo_categoria = tipoCategoriaMap[tipoCategoria];
        const cacheKey = `cache_categorias_${tipoCategoriaMap[tipoCategoria]}`;
        const cache_categorias: any = queryClient.getQueryData(cacheKey);

        const normalize = (list: any[]) =>
          list
            .filter((d: any) => d.flg_inativo !== true)
            .map((dt: any) => ({
              value: dt.cod_subcategoria,
              label: `${dt.descricao}`,
              ...dt,
            }));

        const buildOptions = (optionsRaw: any[]) => {
          const dispesas: any[] = [];
          const receita: any[] = [];
          const outros: any[] = [];

          optionsRaw.forEach((opt: any) => {
            const prefix = opt.label.split(' ', 2)[0];
            if (prefix === '(+)') receita.push(opt);
            else if (prefix === '(-)') dispesas.push(opt);
            else outros.push(opt);
          });

          return [...receita, ...dispesas, ...outros];
        };

        const fetchAmbosFallback = async (baseFields?: any) => {
          const params = { page: pageParam, limit, filter };
          const [{ data: r0 }, { data: r1 }] = await Promise.all([
            api.get(`/categorias/0`, { params }),
            api.get(`/categorias/1`, { params }),
          ]);

          const opts0 = normalize(r0.data ?? []);
          const opts1 = normalize(r1.data ?? []);
          const combined = buildOptions([...opts0, ...opts1]);
          const combinedFiltered = filterCategorias(combined);

          const payload = {
            ...(baseFields || {}),
            data: combined,
            count: String(combined.length),
          };

          queryClient.setQueryData(cacheKey, payload);

          if (retriever) {
            const selectedRetriever = combined.find(
              (d: any) => d.value === retriever,
            );
            if (selectedRetriever) {
              changeSelected({
                value: selectedRetriever.value,
                label: selectedRetriever.label,
              });
            }
          }

          return {
            ...payload,
            data: combinedFiltered,
          };
        };

        if (cache_categorias) {
          if (retriever) {
            const options = cache_categorias.data ?? [];
            const selectedRetriever = options.find(
              (d: any) => d.value === retriever,
            );
            if (selectedRetriever) {
              changeSelected({
                value: selectedRetriever.value,
                label: selectedRetriever.label,
              });
            }
          }

          const filtered = filterCategorias(cache_categorias.data ?? []);

          if (tipo_categoria === 2 && filtered.length === 0) {
            return await fetchAmbosFallback(cache_categorias);
          }

          return { ...cache_categorias, data: filtered };
        }

        const { data: dataFields } = await api.get(
          `/categorias/${tipo_categoria}`,
          {
            params: { page: pageParam, limit, filter },
          },
        );

        setCategoriaTemp(tipo_categoria);

        const options = normalize(dataFields?.data ?? []);

        if (tipo_categoria === 2 && options.length === 0) {
          return await fetchAmbosFallback(dataFields);
        }

        const optionsAux = buildOptions(options);

        queryClient.setQueryData(cacheKey, {
          ...dataFields,
          data: optionsAux,
        });

        if (retriever) {
          const selectedRetriever = optionsAux.find(
            (d: any) => d.value === retriever,
          );
          if (selectedRetriever) {
            changeSelected({
              value: selectedRetriever.value,
              label: selectedRetriever.label,
            });
          }
        }

        return {
          ...dataFields,
          data: optionsAux,
        };
      } catch (error: any) {
        toast.error(error?.data?.message || String(error));
        return {
          count: '0',
          data: [],
          fields: [],
          pagination: {
            lastPage: 0,
            prevPage: 1,
            startPage: 1,
          },
          success: true,
        };
      }
    },
    [
      filter,
      filterCategorias,
      tipoCategoria,
      tipoCategoriaMap,
      queryClient,
      retriever,
      changeSelected,
      limit,
    ],
  );

  const { data, hasNextPage, fetchNextPage, isFetching, refetch } =
    useInfiniteQuery(
      [name, tipoCategoria],
      ({ pageParam = 1 }) => fetchData({ pageParam }),
      {
        refetchOnWindowFocus: false,
      },
    );

  return (
    <InputContainer>
      <Form.Group>
        <Form.Label>{label}</Form.Label>

        <Controller
          name={name}
          control={control}
          defaultValue={{ label: '', value: -1 }}
          render={({ field: { value } }) => {
            const vLabel = (value as any)?.label ?? undefined;

            if (inputRef?.current) {
              if (
                vLabel !== selected.label &&
                inputRef.current.placeholder !== placeholder
              ) {
                inputRef.current.placeholder = placeholder;
              }

              if (vLabel) inputRef.current.placeholder = `${vLabel}`;
              if (!vLabel) inputRef.current.placeholder = placeholder;
            }

            return (
              <>
                <div className="select-container">
                  <label>
                    <input
                      type="text"
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      placeholder={placeholder}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        event.target.value = event.target.value.toUpperCase();
                        setFilter(event.target.value.toUpperCase());

                        if (event.target.value === '') {
                          queryClient.removeQueries(name);
                        }

                        debouncedFn(() => refetch(), 500);
                      }}
                      autoComplete="off"
                      onClick={(event: any) => {
                        if (selected.label) {
                          event.target.placeholder = selected.label;
                        }
                        event.target.value = '';
                        setIsOpen(true);
                      }}
                      ref={inputRef}
                      {...rest}
                    />

                    <div className="drop-indicator">
                      <span role="img" aria-label="open">
                        {filter && isFetching ? (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="spinner"
                          />
                        ) : (
                          <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            role="presentation"
                          >
                            <path
                              d="M8.292 10.293a1.009 1.009 0 000 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 000-1.419.987.987 0 00-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 00-1.406 0z"
                              fill="currentColor"
                              fillRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </div>
                  </label>
                </div>

                {isOpen && (
                  <Lista
                    listaWidth={ListaWidth}
                    inputRef={inputRef}
                    setIsOpen={setIsOpen}
                    setFilter={setFilter}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    data={data}
                    selected={selected}
                    setSelected={setSelected}
                    isFetching={isFetching}
                    queryName={name}
                    changeSelected={changeSelected}
                    queryClient={queryClient}
                    hideNoneOption={hideNoneOption}
                    noneOptionLabel={noneOptionLabel}
                  />
                )}
              </>
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
